import React, {useState} from "react";

import styles from "./BulkUpdateFilePanel.module.css";
import {useAppDispatch} from "../../../../app/hooks";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FileDropzone} from "../../../elements/fileDropzone/FileDropzone";
import {
    showErrorToastNotification,
    showSuccessToastNotification
} from "../../../elements/toastNotification/toastNotificationSlice";
import {saveDownloadedFile} from "../../../../utils/fileTransferUtils";
import {BulkUpdateDefinition} from "../bulkUpdateDefinitions";

const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const BulkUpdateFilePanel: React.FC<{ definition: BulkUpdateDefinition }> = ({definition}) => {
    const dispatch = useAppDispatch();
    const [selectedFile, setSelectedFile] = useState<File | undefined>();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | undefined>();
    const [uploadResult, setUploadResult] = useState<string | undefined>();

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const file = await definition.exportFile();
            saveDownloadedFile(file);
            dispatch(showSuccessToastNotification({message: `Downloaded ${file.fileName}`}));
        } catch (error) {
            dispatch(showErrorToastNotification({
                message: error instanceof Error ? error.message : `Failed to download ${definition.title.toLowerCase()}`
            }));
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileSelected = (file: File) => {
        setSelectedFile(file);
        setUploadError(undefined);
        setUploadResult(undefined);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            return;
        }

        setIsUploading(true);
        setUploadError(undefined);
        try {
            const result = await definition.importFile(selectedFile);
            setUploadResult(result);
            setSelectedFile(undefined);
            dispatch(showSuccessToastNotification({message: result}));
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : `Failed to upload ${definition.title.toLowerCase()}`);
        } finally {
            setIsUploading(false);
        }
    };

    return <div className={styles.wrapper}>
        <section className={`${styles.step} ${styles.downloadStep}`}>
            <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>1</span>
                <span className={styles.stepTitle}>Download the current file</span>
            </div>
            <span className={styles.stepText}>
                Open it in Excel or any spreadsheet tool and change the hours you need.
            </span>
            <div className={styles.downloadAction}>
                <Button
                    title={"Download current file"}
                    iconSrc={"/exportIcon.svg"}
                    state={isDownloading || isUploading ? ButtonState.DISABLED : ButtonState.SECONDARY}
                    handleClick={handleDownload}
                />
            </div>
        </section>

        <section className={`${styles.step} ${styles.uploadStep}`}>
            <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>2</span>
                <span className={styles.stepTitle}>Upload the edited file</span>
            </div>
            <ul className={styles.hints}>
                {definition.hints.map(hint => <li key={hint}>{hint}</li>)}
            </ul>

            <FileDropzone
                accept={".csv"}
                maxSizeBytes={MAX_FILE_BYTES}
                title={selectedFile ? selectedFile.name : "Drop the edited file here, or click to browse"}
                subtitle={selectedFile ? "Select a different file to replace this one" : "CSV file"}
                onFileSelected={handleFileSelected}
                disabled={isUploading}
            />

            {uploadError && <span className={styles.error} role="alert">{uploadError}</span>}
            {uploadResult && <span className={styles.success} role="status">{uploadResult}</span>}

            <div className={styles.buttonsWrapper}>
                <Button
                    title={"Upload and apply"}
                    iconSrc={"/importIcon.svg"}
                    state={selectedFile && !isUploading ? ButtonState.PRIMARY : ButtonState.DISABLED}
                    handleClick={handleUpload}
                />
            </div>
        </section>
    </div>
}
